import React, { memo } from "react";
import { Box, MainText, MainTitle } from "app/components/common";
import { useSelector } from "react-redux";
import {
  getLanguage,
  getProfileSettingsMbti,
} from "@microdoing/shared/services/profileSettings/selectors";
import i18n from "@microdoing/shared/i18n";
import { Dimensions, Image } from "react-native";
import theme from "app/Theme/Theme";
import { getUserFirstName } from "@microdoing/shared/services/user/selectors";
import Animated, {
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withSpring,
} from "react-native-reanimated";
import { isWeb } from "@microdoing/shared/utils/deviceDetectors";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import { getMbtiOptions } from "../../../UserProfile/MbtiSelectorScreen/MbtiLibrary";

const Jouvie3D = require("../../../../../assets/images/Jouvie3D.png");

const { height, width } = Dimensions.get("window");

interface IProps {
  scrollOffsetValue?: { value: any };
}

const DailyWelcome = ({ scrollOffsetValue }: IProps) => {
  const firstName = useSelector(getUserFirstName);
  const userMbti = useSelector(getProfileSettingsMbti);

  const language = useSelector(getLanguage);

  const jouviePositionY = useSharedValue(0);
  const jouviePositionX = useSharedValue(0);
  const scale = useSharedValue(1);

  const jouvieAnimatedStyle = useAnimatedStyle(() => {
    if (isWeb) return {};
    return {
      transform: [
        {
          translateY: withSpring(
            interpolate(
              scrollOffsetValue?.value,
              [0, 150, 250],
              [0, -25, -40],
            ) + interpolate(jouviePositionY.value, [-350, 350], [-250, 250]),
          ),
        },
        {
          translateX: withSpring(
            interpolate(
              jouviePositionX.value,
              [-width, 0, 100],
              [-width + 70, 0, 200],
            ),
          ),
        },
        {
          scale: scale.value,
        },
      ],
    };
  });

  const panGesture = Gesture.Pan()
    .onBegin(() => {})
    .onUpdate(e => {
      jouviePositionX.value = e.translationX;
      jouviePositionY.value = e.translationY;
    })
    .onEnd(e => {
      jouviePositionX.value = 0;
      jouviePositionY.value = 0;
    })
    .onFinalize(() => {});

  const tapGesture = Gesture.Tap()
    .maxDuration(200)
    .onStart(() => {
      jouviePositionY.value = -55;
      jouviePositionY.value = withDelay(
        350,
        withSpring(0, {
          stiffness: 350,
          overshootClamping: true,
        }),
      );
    });

  const pinchGesture = Gesture.Pinch()
    .onStart(() => {})
    .onUpdate(e => {
      scale.value = e.scale;
    })
    .onEnd(() => {
      scale.value = withSpring(1);
    });

  const gestures = Gesture.Exclusive(panGesture, pinchGesture, tapGesture);
  const userMbtiData = getMbtiOptions(language).find(
    item => item.key === userMbti,
  );

  return (
    <Box
      zIndex={1}
      width="100%"
      paddingHorizontal={{ phone: "xm", smallWeb: "l" }}
      flexDirection="row"
      justifyContent="space-between"
      position="relative"
      top={0}
      marginTop="xxs"
    >
      <Box maxWidth="100%">
        <MainTitle variant="l8-bold" numberOfLines={1}>
          {i18n.t("daily-welcome", { firstName })}
        </MainTitle>
        <MainText marginTop="xxs" variant="l4-medium">
          {i18n.t("daily-take-action")}
        </MainText>
      </Box>
      <GestureDetector gesture={gestures}>
        <Box
          padding="m"
          position={{ phone: "absolute", smallWeb: "relative" }}
          width={{ phone: "100%", smallWeb: "auto" }}
          bottom={{ phone: 0 }}
          left={{ phone: theme.spacing.xm }}
          alignItems="flex-end"
          zIndex={-1}
        >
          <Animated.View
            style={[
              {
                width: 70,
                height: 100,
                position: "absolute",
                bottom: 35,
                right: 20,
              },
              jouvieAnimatedStyle,
            ]}
          >
            <Image
              resizeMode="contain"
              source={!userMbti ? Jouvie3D : userMbtiData?.image}
              style={{ height: 100, width: 100, marginTop: 80 }}
            />
          </Animated.View>
        </Box>
      </GestureDetector>
    </Box>
  );
};

export default memo(DailyWelcome);
